import * as React from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'
import { Check, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface FloatingSelectOption {
	value: string
	label: string
}

export interface FloatingSelectProps {
	label: string
	value?: string
	onValueChange?: (value: string) => void
	options: FloatingSelectOption[]
	placeholder?: string
	disabled?: boolean
	className?: string
	id?: string
}

const FloatingSelect = React.forwardRef<HTMLButtonElement, FloatingSelectProps>(
	({ label, value, onValueChange, options, placeholder, disabled, className, id }, ref) => {
		const selectId = id || `floating-select-${label.toLowerCase().replace(/\s+/g, '-')}`
		const hasValue = value && value.length > 0

		return (
			<div className="relative">
				<SelectPrimitive.Root value={value} onValueChange={onValueChange} disabled={disabled}>
					<SelectPrimitive.Trigger
						ref={ref}
						id={selectId}
						className={cn(
							'peer w-full h-14 px-4 pt-5 pb-2 rounded-lg border bg-[#2F2F32] border-[#4C4C4C] text-[#ffffff] text-base text-left',
							'flex items-center justify-between',
							'focus:outline-none focus:ring-2 focus:ring-[#EF4444] focus:border-transparent',
							'disabled:cursor-not-allowed disabled:opacity-50',
							'transition-colors',
							'[&>span]:line-clamp-1',
							className
						)}
					>
						<SelectPrimitive.Value placeholder={placeholder || ' '} />
						<SelectPrimitive.Icon asChild>
							<ChevronDown className="h-4 w-4 text-[#888888]" />
						</SelectPrimitive.Icon>
					</SelectPrimitive.Trigger>

					<SelectPrimitive.Portal>
						<SelectPrimitive.Content
							className={cn(
								'relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-lg border',
								'bg-[#2F2F32] border-[#4C4C4C] text-[#ffffff]',
								'shadow-md',
								'data-[state=open]:animate-in data-[state=closed]:animate-out',
								'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
								'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
								'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
								'data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1'
							)}
							position="popper"
						>
							<SelectPrimitive.Viewport
								className={cn(
									'p-1',
									'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]'
								)}
							>
								{options.map((option) => (
									<SelectPrimitive.Item
										key={option.value}
										value={option.value}
										className={cn(
											'relative flex w-full cursor-pointer select-none items-center',
											'rounded-md py-2.5 pl-8 pr-2 text-sm outline-none',
											'text-[#ffffff]',
											'focus:bg-[#4C4C4C] focus:text-[#ffffff]',
											'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
											'data-[state=checked]:bg-[#4C4C4C]'
										)}
									>
										<span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
											<SelectPrimitive.ItemIndicator>
												<Check className="h-4 w-4 text-[#EF4444]" />
											</SelectPrimitive.ItemIndicator>
										</span>
										<SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
									</SelectPrimitive.Item>
								))}
							</SelectPrimitive.Viewport>
						</SelectPrimitive.Content>
					</SelectPrimitive.Portal>
				</SelectPrimitive.Root>

				<label
					htmlFor={selectId}
					className={cn(
						'absolute left-4 text-[#888888] transition-all duration-200 pointer-events-none',
						hasValue
							? 'top-2 text-xs text-[#BABABB]'
							: 'top-1/2 -translate-y-1/2 text-base'
					)}
				>
					{label}
				</label>
			</div>
		)
	}
)
FloatingSelect.displayName = 'FloatingSelect'

export { FloatingSelect }
